/**
 * Profile Domain Definition (domain-def@4) — the narrative self.
 *
 * `CandidateProfile` (domain-model.md §3.2) — identity, motivations, values /
 * anti-values, trajectory, constraints, voice. Drives the presentation passes.
 * Deliberately split from the structured `Cv` (the proof layer). Seeded by
 * ingest, enriched by Workshop W1; each facet links to `EvidenceClaim`s by URN.
 *
 * Stream: `Agg::career::{userId}::Profile` (per-user singleton). NOTE the
 * re-derivation: the Evidence ledger no longer rides this stream — it has its
 * own `…::Evidence` stream (fixes the claim/profile ordering coupling, critique 6).
 */

import type { DomainDefinitionJSON } from "./schema"

const profileDomain = {
  schemaVersion: "domain-def@4",
  name: "Profile",
  version: "1.0.0",
  description:
    "CandidateProfile — the narrative self (identity, motivations, drives, values, anti-values, trajectory, constraints, voice) that drives the presentation passes",

  types: {
    /** Work-mode preference */
    WorkMode: "'remote' | 'hybrid' | 'onsite'",

    /** A narrative facet, each linkable to evidence-ledger claims */
    ProfileFacet: {
      "+": "reject",
      statement: "NonEmptyString",
      "claimRefs?": "Urn[]",
    },

    /** Identity / throughline (the letter's axis) */
    ProfileIdentity: {
      "+": "reject",
      throughline: "string | null",
      selfDescription: "string | null",
    },

    /** Trajectory — now / mid-term / long-term */
    ProfileTrajectory: {
      "+": "reject",
      now: "string | null",
      midTerm: "string | null",
      longTerm: "string | null",
    },

    /** Constraints driving the location / anti-value gate downstream */
    ProfileConstraints: {
      "+": "reject",
      location: "string | null",
      salaryTarget: "string | null",
      "stabilitySought?": "string | null",
      workModes: "WorkMode[]",
    },

    CandidateProfile: {
      "+": "reject",
      owner: "UserId",
      identity: "ProfileIdentity",
      motivations: "ProfileFacet[]",
      drives: "ProfileFacet[]",
      values: "ProfileFacet[]",
      antiValues: "ProfileFacet[]",
      dealBreakers: "ProfileFacet[]",
      trajectory: "ProfileTrajectory",
      constraints: "ProfileConstraints",
      locale: "Locale",
      "voiceProfile?": "VoiceProfile | null",
    },

    /** Which list-valued facet field an enrichment targets */
    ProfileFacetField:
      "'motivations' | 'drives' | 'values' | 'antiValues' | 'dealBreakers'",

    // ─── Event payloads ──────────────────────────────────────────

    /** Ingest seeds the first structured profile from the raw bundle */
    ProfileSeededPayload: {
      "+": "reject",
      owner: "UserId",
      profile: "CandidateProfile",
      "prov?": "ProvenanceBundle",
    },

    /** Workshop / ingest adds or updates one narrative facet (typed, source-attributed) */
    ProfileFacetEnrichedPayload: {
      "+": "reject",
      owner: "UserId",
      field: "ProfileFacetField",
      facet: "ProfileFacet",
      "prov?": "ProvenanceBundle",
    },

    ProfileIdentityRevisedPayload: {
      "+": "reject",
      owner: "UserId",
      identity: "ProfileIdentity",
    },

    ProfileConstraintsUpdatedPayload: {
      "+": "reject",
      owner: "UserId",
      constraints: "ProfileConstraints",
    },

    ProfileVoiceUpdatedPayload: {
      "+": "reject",
      owner: "UserId",
      voiceProfile: "VoiceProfile",
    },

    /**
     * The presented German Kandidatenprofil narrative (PRESENTATION pass output,
     * domain-model.md §3.2 amendment / llm-workflows.md §4.1). Same shape class as
     * `organization.Summarized` / `opportunity.Briefed`: a `PresentedText`
     * (`{ body, provenance, meta }`) authored from the CONFIRMED evidence ledger — a
     * later moment than `profile.Seeded` (raw seed at ingest time), so no prior event
     * could carry presented prose. Provenance refs ⊆ confirmed claim URNs (structural
     * validation hard-fails); degradation = deterministic composer, SAME event shape.
     */
    ProfileNarrativeAuthoredPayload: {
      "+": "reject",
      owner: "UserId",
      narrative: "PresentedText",
      authoredAt: "Timestamp",
    },
  },

  resources: {
    profile: {
      description: "The calling user's candidate profile",
      type: "CandidateProfile",
      urnPattern: "urn:career:profile:{userId}",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/profile" },
      authz: { visibility: "user-owned", scopes: ["profile:read"] },
    },
  },

  eventStream: {
    streamPattern: "Agg::career::{userId}::Profile",
    events: {
      "profile.Seeded": {
        description: "Ingest produced the first structured profile from the raw bundle",
        type: "ProfileSeededPayload",
      },
      "profile.FacetEnriched": {
        description: "Workshop / ingest added or updated a narrative facet (typed, source-attributed)",
        type: "ProfileFacetEnrichedPayload",
      },
      "profile.IdentityRevised": {
        description: "The identity throughline / self-description was revised (user-owned)",
        type: "ProfileIdentityRevisedPayload",
      },
      "profile.ConstraintsUpdated": {
        description: "Location / salary / stability / work-mode constraints were updated (user-owned)",
        type: "ProfileConstraintsUpdatedPayload",
      },
      "profile.VoiceUpdated": {
        description: "The per-user voice profile (writing samples for voice preservation) was updated",
        type: "ProfileVoiceUpdatedPayload",
      },
      "profile.NarrativeAuthored": {
        description:
          "A presentation pass authored the German Kandidatenprofil narrative from the confirmed evidence ledger (PresentationMeta + provenance map)",
        type: "ProfileNarrativeAuthoredPayload",
      },
    },
  },

  authz: {
    scopes: {
      "profile:read": { description: "Read the owner's candidate profile" },
      "profile:write": { description: "Seed, enrich, or revise the candidate profile" },
    },
    policies: {
      "owner-only": {
        kind: "owner",
        description: "The profile is owner-scoped to the calling Subject (law C1)",
      },
    },
    defaults: {
      unauthenticatedAccess: "deny",
      missingRolesBehavior: "deny",
    },
  },
} as const satisfies DomainDefinitionJSON

export default profileDomain
